import { WeaveButton } from '@/components/UI/WeaveButton/WeaveButton';
import styles from './styles.module.scss';
import * as XLSX from 'xlsx';
import React, { useMemo, useRef, useState } from 'react';
import { RoomImport } from './RoomImport';
import { RoomSubHeader, RoomHeader, ParsedData, RoomData } from './SpacePlanningTypes';
import { Room } from './Room';
import { useSpacePlanningStore } from '@/store/spacePlanningStore';
import { CustomSelectEvent, Select } from '@/components/UI/Select/Select';
import { unitConversionService as unitService } from '@/services/unit-conversion.service';

export interface RoomOption {
	id: string;
	label: string;
	value: number;
	totalASF: number;
	wallAreaTotal: number;
	height: number;
	level?: number;
}

export interface RoomTypeProps {
	path: string;
	roomTypes: RoomOption[];
	allowRoomEditing?: boolean;
	visibleLevels?: number[];
}

export const RoomType: React.FC<RoomTypeProps> = ({
	path,
	roomTypes,
	allowRoomEditing = true,
	visibleLevels,
}) => {
	const { roomTypeContainer } = styles;
	const [excelData, setExcelData] = useState<ParsedData[]>([]);
	const [selectedRoomId, setSelectedRoomId] = useState<string | null>(roomTypes[0]?.id ?? null);
	const [roomListMode, setRoomListMode] = useState<'detailed' | 'grouped'>('detailed');
	const { spaces, addRooms } = useSpacePlanningStore();

	const allRoomsForPath = spaces[path]?.rooms || [];
	const roomsForPath =
		visibleLevels && visibleLevels.length > 0
			? allRoomsForPath.filter(
					(room) =>
						typeof room.level === 'number' && visibleLevels.includes(Number(room.level))
				)
			: allRoomsForPath;
	const input = useRef<HTMLInputElement>(null);
	const hasMultipleVisibleLevels = Boolean(visibleLevels && visibleLevels.length > 1);
	const groupedRoomSummary = useMemo(() => {
		const grouped = new Map<string, { count: number; totalArea: number }>();

		roomsForPath.forEach((room) => {
			const label = room.spaceDescription?.trim() || 'Unassigned';
			const current = grouped.get(label) ?? { count: 0, totalArea: 0 };
			current.count += room.quantity;
			current.totalArea += room.ASF * room.quantity;
			grouped.set(label, current);
		});

		return Array.from(grouped.entries())
			.map(([label, data]) => ({
				label,
				count: data.count,
				totalArea: data.totalArea,
			}))
			.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
	}, [roomsForPath]);

	const parseJsonData = (data: unknown[]) => {
		const parsedData: ParsedData[] = [];

		let currentMainHeader: RoomHeader | null = null;
		let currentSubHeader: RoomSubHeader | null = null;
		let currentRoomData: RoomData[] = [];

		for (let i = 0; i < data.length; i++) {
			const row = data[i] as Record<string, string | number | undefined>;

			// Check if this is a main header (has "Space/Description" and "Total ASF" but no "Space #")
			if (
				row['Space/Description'] &&
				row['Total ASF'] &&
				!row['Space #'] &&
				typeof row['Total ASF'] === 'number'
			) {
				// Save previous group if exists
				if (currentMainHeader && currentSubHeader) {
					parsedData.push({
						space: {
							header: currentMainHeader,
							sub: {
								header: currentSubHeader,
								data: [...currentRoomData],
							},
						},
					});
				}

				// Start new main header
				currentMainHeader = {
					spaceDescription: row['Space/Description'] as string,
					totalASF: row['Total ASF'],
				};
				currentSubHeader = null;
				currentRoomData = [];
			}
			// Check if this is a sub-header (has "Space #", "Space/Description", "Total ASF" but no detailed room data)
			else if (
				row['Space #'] &&
				row['Space/Description'] &&
				row['Total ASF'] &&
				!row['ASF'] &&
				!row['Occupancy'] &&
				!row['Quantity']
			) {
				// Save previous sub-group if exists
				if (currentMainHeader && currentSubHeader && currentRoomData.length > 0) {
					parsedData.push({
						space: {
							header: currentMainHeader,
							sub: {
								header: currentSubHeader,
								data: [...currentRoomData],
							},
						},
					});
					currentRoomData = [];
				}

				// Start new sub-header
				currentSubHeader = {
					spaceId: row['Space #'] as number,
					spaceDescription: row['Space/Description'] as string,
					totalASF: row['Total ASF'] as number,
				};
			}
			// Check if this is room data (has all the detailed fields)
			else if (
				row['Space #'] &&
				row['Space/Description'] &&
				(row['ASF'] !== undefined ||
					row['Occupancy'] !== undefined ||
					row['Quantity'] !== undefined)
			) {
				// If we don't have a sub-header but have a main header, create a default sub-header
				if (currentMainHeader && !currentSubHeader) {
					currentSubHeader = {
						spaceId: row['Space #'] as number,
						spaceDescription: row['Space/Description'] as string,
						totalASF: (row['Total ASF'] as number) || 0,
					};
				}
				// Add room data
				const roomData: RoomData = {
					id: row['id'] as string,
					spaceId: row['Space #'] as number,
					spaceDescription: row['Space/Description'] as string,
					ASF: (row['ASF'] as number) || 0,
					occupancy: (row['Occupancy'] as number) || 0,
					quantity: (row['Quantity'] as number) || 0,
					totalASF: (row['Total ASF'] as number) || 0,
				};

				currentRoomData.push(roomData);
			}
		}

		// Don't forget to save the last group
		if (currentMainHeader && currentSubHeader) {
			parsedData.push({
				space: {
					header: currentMainHeader,
					sub: {
						header: currentSubHeader,
						data: [...currentRoomData],
					},
				},
			});
		}

		return parsedData;
	};

	const loadFile = async () => {
		if (!allowRoomEditing) return;
		if (input.current) {
			input.current.value = '';
			input.current.click();

			input.current.onchange = async (event) => {
				const file = (event.target as HTMLInputElement).files?.[0];
				if (file) {
					const reader = new FileReader();

					reader.onload = (event) => {
						const data = event.target?.result;
						const workbook = XLSX.read(data, { type: 'buffer' });
						const sheetName = workbook.SheetNames[0]; // Get the first sheet
						const worksheet = workbook.Sheets[sheetName];
						const json = XLSX.utils.sheet_to_json(worksheet); // Convert sheet to JSON
						setExcelData(parseJsonData(json));
					};

					reader.readAsArrayBuffer(file);
				}
			};
		}
	};

	const handleRoomTypeChange = (e: CustomSelectEvent) => {
		const val = e.detail.value;
		setSelectedRoomId(val ?? null);
	};

	const handleAddRoom = () => {
		if (!allowRoomEditing) return;
		if (!selectedRoomId) return;

		const selected = roomTypes.find((r) => String(r.id) === String(selectedRoomId));
		if (!selected) {
			return;
		}
		const resolvedLevel =
			typeof selected.level === 'number'
				? selected.level
				: visibleLevels && visibleLevels.length === 1
					? visibleLevels[0]
					: undefined;
		if (hasMultipleVisibleLevels && typeof resolvedLevel !== 'number') {
			return;
		}

		const baseLabel = selected.label.split('(')[0].trim();
		const normalizedLabel = baseLabel.replace(/^Level\s+\d+\s*-\s*/i, '').trim();

		const nextSpaceId =
			allRoomsForPath.reduce((maxId, room) => Math.max(maxId, Number(room.spaceId) || 0), 0) +
			1;
		const newRoom: RoomData = {
			id: selected.id,
			spaceDescription: normalizedLabel,
			totalASF: selected.totalASF,
			wallAreaTotal: selected.wallAreaTotal,
			height: selected.height,
			level: resolvedLevel,
			spaceId: nextSpaceId,
			ASF: Number(selected.value) || 0,
			occupancy: 0,
			quantity: 1,
		};

		addRooms(path, [newRoom]);

		setSelectedRoomId('');
	};

	return (
		<div>
			<div className={styles.roomListModeToggle}>
				<WeaveButton
					variant={roomListMode === 'detailed' ? 'solid' : 'outlined'}
					onClick={() => setRoomListMode('detailed')}
				>
					Detailed
				</WeaveButton>
				<WeaveButton
					variant={roomListMode === 'grouped' ? 'solid' : 'outlined'}
					onClick={() => setRoomListMode('grouped')}
				>
					Summary
				</WeaveButton>
			</div>
			{roomListMode === 'detailed' ? (
				<label className={styles.formGroupLabel}>Choose or import a room</label>
			) : null}

			{roomListMode === 'detailed' ? (
				<>
					<div className={roomTypeContainer}>
						<div style={{ width: '100%' }}>
							<Select
								value={selectedRoomId ?? ''}
								onChange={handleRoomTypeChange}
								options={roomTypes.map((rt) => ({
									label: rt.label,
									value: String(rt.id),
								}))}
							/>
						</div>
						<WeaveButton
							variant="solid"
							disabled={!selectedRoomId || !allowRoomEditing}
							onClick={handleAddRoom}
						>
							Add Room
						</WeaveButton>
						<WeaveButton
							variant="outlined"
							onClick={loadFile}
							disabled={!allowRoomEditing}
						>
							Import
						</WeaveButton>
						<input
							ref={input}
							style={{ display: 'none' }}
							type="file"
							id="buildingProgram"
							name="buildingProgram"
							accept=" application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
						/>
						<RoomImport data={excelData} path={path} />
					</div>
					{roomsForPath.map((room) => (
						<Room
							key={`${path}-${room.spaceId}-${room.spaceDescription}-${room.ASF}-${room.quantity}`}
							data={room}
							path={path}
							allowEditing={allowRoomEditing}
							showLevelInTitle={Boolean(visibleLevels && visibleLevels.length > 1)}
						/>
					))}
				</>
			) : (
				<div className={styles.groupedSummaryCard}>
					<div className={styles.groupedSummaryTitle}>Summary by typology</div>
					{groupedRoomSummary.length === 0 ? (
						<p className={styles.groupedSummaryEmpty}>No rooms added yet.</p>
					) : (
						<ul className={styles.groupedSummaryList}>
							{groupedRoomSummary.map((item) => (
								<li
									key={`${path}-${item.label}`}
									className={styles.groupedSummaryItem}
								>
									<span className={styles.groupedSummaryLabel}>
										{item.label} x{item.count}
									</span>
									<span className={styles.groupedSummaryArea}>
										{unitService.formatWithPreferredUnit(
											item.totalArea,
											'area',
											'm2'
										)}
									</span>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</div>
	);
};
